import invert from 'lodash/invert';
import findKey from 'lodash/findKey';

import {
  DATASET_MIXPANEL,
  PropertyType as PropertyFilterPropertyType,
  ResourceType as PropertyFilterResourceType,
} from 'common/report/constants';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {InsightsResourceType} from 'common/types/reports/bookmark';
import {PropertyFilterDateRange} from 'common/widgets/date-range-picker/models/property-filter-date-range';
import {FilterClause as FilterClauseType} from 'common/report/insights/util/types';

import {PropertyFilterFactory} from './utils';
import {
  BooleanOperator as PropertyFilterBooleanOperator,
  DisplayContext as PropertyFilterDisplayContext,
  ListOperator,
} from './types';

// TODO: Replace these mappings below with enums in ./types.ts

const INSIGHTS_PROPERTY_TYPES = {
  STRING: `string`,
  NUMBER: `number`,
  DATETIME: `datetime`,
  BOOLEAN: `boolean`,
  LIST: `list`,
};

export const INSIGHTS_STRING_TYPES = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
  EQUALS: `equals`,
  DOES_NOT_EQUAL: `does not equal`,
  IS_SET: `is set`,
  IS_NOT_SET: `is not set`,
};

export const INSIGHTS_NUMBER_TYPES = {
  IS_BETWEEN: `is between`,
  IS_NOT_BETWEEN: `is not between`,
  IS_AT_LEAST: `is at least`,
  IS_AT_MOST: `is at most`,
  IS_GREATER_THAN: `is greater than`,
  IS_LESS_THAN: `is less than`,
  IS_EQUAL_TO: `is equal to`,
  IS_NOT_EQUAL_TO: `is not equal to`,
  IS_SET: `is set`,
  IS_NOT_SET: `is not set`,
};

export const INSIGHTS_LIST_TYPES = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
};

export const INSIGHTS_DATE_RANGE_TYPES = {
  WAS_ON: `was on`,
  WAS_NOT_ON: `was not on`,
  WAS_BETWEEN: `was between`,
  WAS_NOT_BETWEEN: `was not between`,
  WAS_LESS_THAN: `was less than`,
  WAS_IN_THE: `was in the`,
  WAS_NOT_IN_THE: `was not in the`,
  WAS_BEFORE: `was before`,
  WAS_SINCE: `was since`,
  WAS_IN_THE_NEXT: `was in the next`,
};

export const INSIGHTS_BOOLEAN_TYPES = {
  IS_TRUE: `is true`,
  IS_FALSE: `is false`,
};

export const INSIGHTS_COHORT_TYPE = `$cohorts`;

const INSIGHTS_BOOLEAN_MAPPING = {
  [INSIGHTS_BOOLEAN_TYPES.IS_TRUE]: PropertyFilterBooleanOperator.True,
  [INSIGHTS_BOOLEAN_TYPES.IS_FALSE]: PropertyFilterBooleanOperator.False,
};

const INSIGHTS_DATE_RANGE_MAPPING = {
  [INSIGHTS_DATE_RANGE_TYPES.WAS_ON]: DateRangeType.On,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_ON]: DateRangeType.NotOn,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_BETWEEN]: DateRangeType.Between,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_BETWEEN]: DateRangeType.NotBetween,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_LESS_THAN]: DateRangeType.RelativeBefore,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE]: DateRangeType.RelativeAfter,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_IN_THE]: DateRangeType.NotRelativeAfter,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_BEFORE]: DateRangeType.Before,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_SINCE]: DateRangeType.Since,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE_NEXT]: DateRangeType.RelativeFuture,
};

export const INSIGHTS_DATE_RANGE_NEGATION_MAPPING = {
  [INSIGHTS_DATE_RANGE_TYPES.WAS_ON]: INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_ON,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_ON]: INSIGHTS_DATE_RANGE_TYPES.WAS_ON,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_BETWEEN]: INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_BETWEEN,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_BETWEEN]: INSIGHTS_DATE_RANGE_TYPES.WAS_BETWEEN,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_LESS_THAN]: INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE_NEXT,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE_NEXT]: INSIGHTS_DATE_RANGE_TYPES.WAS_LESS_THAN,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE]: INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_IN_THE,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_NOT_IN_THE]: INSIGHTS_DATE_RANGE_TYPES.WAS_IN_THE,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_BEFORE]: INSIGHTS_DATE_RANGE_TYPES.WAS_SINCE,
  [INSIGHTS_DATE_RANGE_TYPES.WAS_SINCE]: INSIGHTS_DATE_RANGE_TYPES.WAS_BEFORE,
};

export const INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING = {
  [InsightsResourceType.Events]: PropertyFilterResourceType.Event,
  [InsightsResourceType.People]: PropertyFilterResourceType.User,
  [InsightsResourceType.Other]: PropertyFilterResourceType.Other,
};

export const QUERY_BUILDER_RESOURCE_TYPE_MAPPING = invert(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING);

export function convertFilterParamsToDateRange({
  filterOperator,
  filterValue,
  filterDateUnit = null,
}: {
  filterOperator: TODO;
  filterValue: TODO;
  filterDateUnit?: TimeUnit | null;
}) {
  const type = INSIGHTS_DATE_RANGE_MAPPING[filterOperator];

  switch (type) {
    case DateRangeType.RelativeAfter:
    case DateRangeType.RelativeFuture:
    case DateRangeType.NotRelativeAfter:
    case DateRangeType.RelativeBefore:
      return new PropertyFilterDateRange({type, window: {value: filterValue, unit: filterDateUnit as TimeUnit}});
    case DateRangeType.Between:
    case DateRangeType.NotBetween:
      return new PropertyFilterDateRange({type, from: filterValue[0], to: filterValue[1]});
    case DateRangeType.On:
    case DateRangeType.NotOn:
      return new PropertyFilterDateRange({type, from: filterValue, to: filterValue});
    case DateRangeType.Before:
      return new PropertyFilterDateRange({type, to: filterValue});
    case DateRangeType.Since:
      return new PropertyFilterDateRange({type, from: filterValue});
    default:
      return null;
  }
}

export function convertDateRangeToFilterParams(dateRange: TODO) {
  const filterOperator = findKey(INSIGHTS_DATE_RANGE_MAPPING, (type) => type === dateRange.type);
  let filterValue = null;
  let filterDateUnit = null;
  switch (dateRange.type) {
    case DateRangeType.RelativeFuture:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter:
    case DateRangeType.RelativeBefore:
      filterDateUnit = dateRange.window.unit;
      filterValue = dateRange.window.value;
      break;
    case DateRangeType.Between:
    case DateRangeType.NotBetween:
      filterValue = [dateRange.from, dateRange.to];
      break;
    case DateRangeType.On:
    case DateRangeType.NotOn:
    case DateRangeType.Since:
      filterValue = dateRange.from;
      break;
    case DateRangeType.Before:
      filterValue = dateRange.to;
      break;
    default:
      return null;
  }

  return {
    filterOperator,
    filterValue,
    ...(filterDateUnit ? {filterDateUnit} : {}),
  };
}

export function insightsFilterAttrsFromPropertyFilter(propertyFilter: TODO): FilterClauseType {
  const filterType = propertyFilter.propertyType || propertyFilter.filterType;
  const filterJoinType = propertyFilter.joinPropertyType || propertyFilter.filterJoinType;
  let filterParams: TODO = null;

  if (propertyFilter.resourceType === PropertyFilterResourceType.Cohort) {
    filterParams = {
      filterOperator: propertyFilter.filterOperator,
      filterValue: propertyFilter.filterValue,
      value: INSIGHTS_COHORT_TYPE,
    };
  } else {
    switch (filterType) {
      case PropertyFilterPropertyType.Datetime:
        filterParams = convertDateRangeToFilterParams(propertyFilter.filterValue);
        break;
      case PropertyFilterPropertyType.Boolean:
        filterParams = {
          filterOperator: findKey(INSIGHTS_BOOLEAN_MAPPING, (o) => o === propertyFilter.filterOperator),
          filterValue: null,
        };
        break;
      case PropertyFilterPropertyType.List:
        filterParams = {
          filterOperator: propertyFilter.filterOperator,
          filterValue: propertyFilter.filterValue,
        };
        break;
      default:
        {
          filterParams = {
            filterOperator: propertyFilter.filterOperator,
            filterValue: propertyFilter.filterValue,
          };

          if (propertyFilter.filterDateUnit) {
            filterParams[`filterDateUnit`] = propertyFilter.filterDateUnit;
          }
        }
        break;
    }
  }

  // NOTE: We return an object of attrs rather than returning a FilterClause object because PropertyFilter
  // is a subset of an insights FilterClause (e.g. missing profileType).
  return {
    filterType,
    dataset: propertyFilter.datasetId || DATASET_MIXPANEL,
    resourceType: findKey(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING, (v) => v === propertyFilter.resourceType),
    value: propertyFilter.propertyName || filterParams?.value || propertyFilter.value,
    ...filterParams,
    ...(propertyFilter.propertyObjectKey ? {propertyObjectKey: propertyFilter.propertyObjectKey} : {}),
    ...(filterJoinType ? {filterJoinType} : {}),
    ...(`startStepNumber` in propertyFilter ? {startStepNumber: propertyFilter.startStepNumber} : {}),
    ...(`endStepNumber` in propertyFilter ? {endStepNumber: propertyFilter.endStepNumber} : {}),
    ...(propertyFilter.behavior ? {behavior: propertyFilter.behavior} : {}),
    ...(`scdEvent` in propertyFilter ? {scdEvent: propertyFilter.scdEvent} : {}),
    ...(`scdOptions` in propertyFilter ? {scdOptions: propertyFilter.scdOptions} : {}),
    ...(`experiments` in propertyFilter ? {experiments: propertyFilter.experiments} : {}),
    ...(propertyFilter.listInclusion ? {listInclusion: propertyFilter.listInclusion} : {}),
    ...(propertyFilter.listQuantifier ? {listQuantifier: propertyFilter.listQuantifier} : {}),
    ...(propertyFilter.listItemFilters
      ? {listItemFilters: propertyFilter.listItemFilters.map(insightsFilterAttrsFromPropertyFilter)}
      : {}),
    ...(propertyFilter.customProperty ? {customProperty: propertyFilter.customProperty} : {}),
    ...(propertyFilter.customPropertyId ? {customPropertyId: propertyFilter.customPropertyId} : {}),
    ...(propertyFilter.dataGroupId ? {dataGroupId: propertyFilter.dataGroupId} : {}),
    ...(propertyFilter.tempDataGroupId ? {tempDataGroupId: propertyFilter.tempDataGroupId} : {}),
    ...(propertyFilter.propertyDefaultType ? {defaultType: propertyFilter.propertyDefaultType} : {}),
    ...(propertyFilter.profileHistoryOptions ? {profileHistoryOptions: propertyFilter.profileHistoryOptions} : {}),
    ...(propertyFilter.isHidden ? {isHidden: propertyFilter.isHidden} : {}),
    ...(propertyFilter.isInherited ? {isInherited: propertyFilter.isInherited} : {}),
  };
}

/**
 * Coerces a persisted `$cohorts` filter value entry into the `{cohort: {...}}` shape the query builder
 * expects. Query API accepts a bare cohort id, a bare saved-cohort reference, or a bare raw cohort
 * definition in this position (see `convert_to_cohort_tree_node` in api/version_2_0/insights/params_util.py),
 * so bookmarks written by other clients can carry those shapes even though the UI only writes the wrapped form.
 */
function normalizeCohortFilterValueEntry(entry: TODO) {
  if (typeof entry === `number` || typeof entry === `string`) {
    const cohortId = Number(entry);
    return {cohort: {id: Number.isInteger(cohortId) ? cohortId : entry}};
  }

  if (!entry || typeof entry !== `object` || `cohort` in entry) {
    return entry;
  }

  if (Number.isInteger(entry.id) || entry.raw_cohort) {
    return {cohort: entry};
  }

  return {cohort: {name: entry.name, negated: false, raw_cohort: entry}};
}

// TODO opslead
export function insightsFilterToPropertyFilter(filter: TODO) {
  let filterValue = null;
  let filterOperator = null;

  if (filter.value && filter.value === INSIGHTS_COHORT_TYPE) {
    return PropertyFilterFactory.createPropertyFilter(
      {
        ...(filter.isHidden ? {isHidden: filter.isHidden} : {}),
        ...(filter.isInherited ? {isInherited: filter.isInherited} : {}),
        // Query API excludes the cohort only for `does not contain` and includes it for any other
        // operator, so bookmarks written outside the UI can carry e.g. `equals` here.
        filterOperator:
          filter.filterOperator === ListOperator.DoesNotContain ? ListOperator.DoesNotContain : ListOperator.Contains,
        filterValue: Array.isArray(filter.filterValue)
          ? filter.filterValue.map(normalizeCohortFilterValueEntry).filter(Boolean)
          : [],
        propertyType: PropertyFilterPropertyType.List,
        resourceType: PropertyFilterResourceType.Cohort,
      },
      {displayContext: PropertyFilterDisplayContext.Insights},
    );
  }

  if (filter.filterType === INSIGHTS_PROPERTY_TYPES.DATETIME) {
    filterValue = convertFilterParamsToDateRange({
      filterOperator: filter.filterOperator,
      filterValue: filter.filterValue,
      filterDateUnit: filter.filterDateUnit,
    });
    filterOperator = INSIGHTS_DATE_RANGE_MAPPING[filter.filterOperator];
  } else if (filter.filterType === INSIGHTS_PROPERTY_TYPES.BOOLEAN) {
    filterOperator = INSIGHTS_BOOLEAN_MAPPING[filter.filterOperator];
    if (filter.filterOperator === INSIGHTS_BOOLEAN_TYPES.IS_TRUE) {
      filterValue = true;
    } else if (filter.filterOperator === INSIGHTS_BOOLEAN_TYPES.IS_FALSE) {
      filterValue = false;
    }
  } else {
    [filterOperator, filterValue] = [filter.filterOperator, filter.filterValue];
  }

  return PropertyFilterFactory.createPropertyFilter(
    {
      ...(filter.behavior ? {behavior: filter.behavior} : {}),
      ...(filter.customProperty ? {customProperty: filter.customProperty} : {}),
      ...(filter.customPropertyId ? {customPropertyId: filter.customPropertyId} : {}),
      ...(filter.dataGroupId ? {dataGroupId: filter.dataGroupId} : {}),
      ...(filter.tempDataGroupId ? {tempDataGroupId: filter.tempDataGroupId} : {}),
      ...(filter.filterDateUnit ? {filterDateUnit: filter.filterDateUnit} : {}),
      ...(filter.filterJoinType ? {joinPropertyType: filter.filterJoinType} : {}),
      ...(`startStepNumber` in filter ? {startStepNumber: filter.startStepNumber} : {}),
      ...(`endStepNumber` in filter ? {endStepNumber: filter.endStepNumber} : {}),
      ...(`scdEvent` in filter ? {scdEvent: filter.scdEvent} : {}),
      ...(`scdOptions` in filter ? {scdOptions: filter.scdOptions} : {}),
      ...(`experiments` in filter ? {experiments: filter.experiments} : {}),
      ...(filter.listInclusion ? {listInclusion: filter.listInclusion} : {}),
      ...(filter.listQuantifier ? {listQuantifier: filter.listQuantifier} : {}),
      ...(filter.profileHistoryOptions ? {profileHistoryOptions: filter.profileHistoryOptions} : {}),
      ...(filter.isHidden ? {isHidden: filter.isHidden} : {}),
      ...(filter.isInherited ? {isInherited: filter.isInherited} : {}),
      ...(filter.listItemFilters ? {listItemFilters: filter.listItemFilters.map(insightsFilterToPropertyFilter)} : {}),
      ...(filter.propertyObjectKey ? {propertyObjectKey: filter.propertyObjectKey} : {}),
      ...(filter.defaultType ? {propertyDefaultType: filter.defaultType} : {}),
      datasetId: filter.dataset,
      filterOperator,
      filterValue,
      propertyName: filter.value,
      propertyType: filter.filterType,
      resourceType: (INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING as Record<string, PropertyFilterResourceType>)[
        filter.resourceType
      ],
    },
    {displayContext: PropertyFilterDisplayContext.Insights},
  );
}
